import { Injectable } from '@angular/core';
import { Faro, getWebInstrumentations, initializeFaro } from '@grafana/faro-web-sdk';
import type { Patterns } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { environment } from '@hub-env/environment';
import { truncateCodePoints } from '../auth/unicode-truncate';

// Extract a semantic view name from a URL path.
// e.g., '/admin/airlines' -> 'admin', '/upas/123/edit' -> 'upas'
export function extractViewName(path: string): string {
  const segment = path.split('/').filter(Boolean)[0];
  return segment || 'home';
}

// Normalize a URL path into a route pattern for span aggregation.
// Replaces numeric IDs and UUIDs with :id so metrics/errors group correctly.
// The raw URL is preserved separately in the http.url span attribute (set by OTel).
// e.g., /api/v2/airlines/123                       -> /api/v2/airlines/:id
// e.g., /api/v2/upas/a1b2c3d4-e5f6-7890-abcd-ef01 -> /api/v2/upas/:id
export function normalizeUrlPath(url: string): string {
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    return pathname
      .split('/')
      .map(seg =>
        /^\d+$/.test(seg) ||
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)
          ? ':id'
          : seg
      )
      .join('/');
  } catch {
    return url;
  }
}

// URLs that Faro will inject W3C Trace Context (traceparent/tracestate) on.
// We propagate ONLY to backends we control. The `*.atpco.net` regex covers
// dev/gold/prod deployments. We additionally derive a pattern from
// environment.API_URL so local-dev (https://localhost:4200) also propagates;
// the previous hard-coded `localhost:3000` pattern silently missed the
// Angular dev server.
export function getTracePropagationUrls(): Patterns {
  const patterns: Patterns = [
    /https:\/\/[^/]*\.atpco\.net(?::\d+)?(?:\/|$)/,
  ];

  try {
    const apiUrl = new URL(environment.API_URL);
    const escapedHost = apiUrl.host.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
    patterns.push(new RegExp(`^${apiUrl.protocol}\\/\\/${escapedHost}(?:\\/|$)`));
  } catch {
    // environment.API_URL malformed — fall back to the atpco.net regex above.
  }

  return patterns;
}

@Injectable({ providedIn: 'root' })
export class FaroObservabilityService {
  private faro: Faro | null = null;
  private knownTraceIds = new Set<string>();

  initialize(): Faro | null {
    if (this.faro || !environment.faroCollectorUrl) {
      return this.faro;
    }

    this.faro = initializeFaro({
      url: environment.faroCollectorUrl,
      app: {
        name: environment.faroAppName || 'Routehappy Hub UI',
        version: environment.version,
        environment: environment.name,
      },
      instrumentations: [
        // Mandatory, omits default browser instrumentations otherwise.
        ...getWebInstrumentations(),

        // Tracing package for end-to-end visibility across browser and backend.
        // Faro injects traceparent/tracestate for matching CORS URLs, and Rails
        // Rack OpenTelemetry instrumentation continues the incoming trace.
        new TracingInstrumentation({
          instrumentationOptions: {
            propagateTraceHeaderCorsUrls: getTracePropagationUrls(),
            fetchInstrumentationOptions: {
              applyCustomAttributesOnSpan: (span: any, request: Request | RequestInit, result: Response | any) => {
                const rawUrl = request instanceof Request ? request.url : '';
                if (rawUrl) {
                  const route = normalizeUrlPath(rawUrl);
                  const method = request instanceof Request ? request.method : 'GET';
                  span.setAttribute('http.route', route);
                  span.updateName(`${method} ${route}`);
                }
                if (result instanceof Response) {
                  span.setAttribute('http.status_code', result.status);
                }
              },
            },
            xhrInstrumentationOptions: {
              applyCustomAttributesOnSpan: (span: any, xhr: XMLHttpRequest) => {
                if (xhr.responseURL) {
                  const route = normalizeUrlPath(xhr.responseURL);
                  // XHR does not expose the method here; OTel's default span name
                  // is usually "HTTP GET" or "GET", so extract the method from it.
                  const method = (span.name || '').replace(/^HTTP\s+/, '').split(' ')[0] || 'HTTP';
                  span.setAttribute('http.route', route);
                  span.updateName(`${method} ${route}`);
                }
                span.setAttribute('http.status_code', xhr.status);
              },
            },
          },
          // Resource attributes are attached to every span from this browser session.
          resourceAttributes: {
            'deployment.environment': environment.name,
          },
        }),
      ],
    });

    return this.faro;
  }

  setSessionAttributes(attributes: Record<string, string>): void {
    // Keep session attributes useful for correlation but free of PII.
    if (this.faro) {
      this.faro.api.setSession({ attributes });
    }
  }

  setView(name: string): void {
    if (this.faro) {
      this.faro.api.setView({ name });
    }
  }

  // Push a structured event into Faro so it appears in Grafana alongside spans.
  // Used by MsalTelemetryService for auth-event signals and by
  // ApiTelemetryInterceptor for API failure observations.
  pushEvent(name: string, attributes: Record<string, string> = {}): void {
    if (!this.faro) return;
    try {
      this.faro.api.pushEvent(name, attributes);
    } catch (err) {
      // observability must never break the app
      console.warn('Faro pushEvent failed', err);
    }
  }

  // Record a handled error to Faro (does not throw or alter UI).
  pushError(error: Error, context: Record<string, string> = {}): void {
    if (!this.faro) return;
    try {
      this.faro.api.pushError(error, { context });
    } catch (err) {
      console.warn('Faro pushError failed', err);
    }
  }

  // Tag this browser session with the backend trace_id that surfaced an
  // auth failure (401 or 403). Lets you pivot from a session in Faro to
  // the trace in Tempo without parsing response bodies.
  //
  // When the active MSAL account is known, attach its identity too — during
  // an outage we need to be able to list every user whose API calls were
  // rejected.
  recordApiAuthFailure(opts: {
    traceId?: string | null;
    status: number;
    url: string;
    /**
     * Normalized route template (e.g. /api/v2/airlines/:id) so Faro can
     * aggregate failures across instances of the same endpoint.
     * Caller is expected to have already run normalizeUrlPath; the
     * service will emit it on the event as `http.route`.
     */
    route?: string | null;
    failureReason?: string | null;
    failureClass?: string | null;
    detail?: string | null;
    accountEmail?: string | null;
    accountId?: string | null;
    accountTenantId?: string | null;
    accountUsername?: string | null;
  }): void {
    if (!this.faro) return;

    if (opts.traceId && !this.knownTraceIds.has(opts.traceId)) {
      this.knownTraceIds.add(opts.traceId);
      // Cap the count we keep — a long-lived session could otherwise grow
      // session attributes unboundedly.
      if (this.knownTraceIds.size > 25) {
        const oldest = this.knownTraceIds.values().next().value;
        if (oldest) this.knownTraceIds.delete(oldest);
      }
      const sessionAttrs: Record<string, string> = {
        last_api_auth_failure_trace_id: opts.traceId,
        last_api_auth_failure_status: String(opts.status),
      };
      if (opts.accountEmail) sessionAttrs['account.email'] = opts.accountEmail;
      if (opts.accountId)    sessionAttrs['account.id']    = opts.accountId;
      if (opts.accountTenantId) sessionAttrs['account.tenant_id'] = opts.accountTenantId;
      this.setSessionAttributes(sessionAttrs);
    }

    this.pushEvent('api_auth_failure', {
      'http.status_code': String(opts.status),
      'http.url': opts.url,
      ...(opts.route ? { 'http.route': opts.route } : {}),
      ...(opts.traceId ? { 'trace_id': opts.traceId } : {}),
      ...(opts.failureReason ? { 'auth.failure_reason': opts.failureReason } : {}),
      ...(opts.failureClass ? { 'auth.failure_class': opts.failureClass } : {}),
      // Code-point-safe truncation so non-ASCII detail (e.g. localized server
      // error messages) round-trips cleanly to Faro.
      ...(opts.detail ? { 'auth.detail': truncateCodePoints(opts.detail, 256) } : {}),
      ...(opts.accountEmail ? { 'account.email': opts.accountEmail } : {}),
      ...(opts.accountId ? { 'account.id': opts.accountId } : {}),
      ...(opts.accountTenantId ? { 'account.tenant_id': opts.accountTenantId } : {}),
      ...(opts.accountUsername ? { 'account.username': opts.accountUsername } : {}),
    });
  }
}

export function initializeFaroObservability(faroObservabilityService: FaroObservabilityService) {
  // Initialize before AppComponent creates the first app API request.
  return () => {
    faroObservabilityService.initialize();
  };
}
